"use client";

import { useState, type ReactElement } from "react";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import Link from "next/link";
import {
  BENCHMARK_CONTROL_COLOR,
  BENCHMARK_TREATMENT_COLOR,
  BENCHMARK_TOOLTIP_CONTROL_SECONDS,
  BENCHMARK_TOOLTIP_TREATMENT_SECONDS,
  BENCHMARK_TOOLTIP_MAX_SECONDS,
  BENCHMARK_TOOLTIP_SPEEDUP_FACTOR,
  TOOLTIP_HOVER_DELAY_MS,
} from "@/constants";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

interface BenchmarkTooltipProps {
  href: string;
  children: React.ReactNode;
  className?: string;
}

interface MiniBarProps {
  targetSeconds: number;
  maxSeconds: number;
  color: string;
  label: string;
  isAnimating: boolean;
  shouldReduceMotion?: boolean;
}

const MiniBar = ({
  targetSeconds,
  maxSeconds,
  color,
  label,
  isAnimating,
  shouldReduceMotion = false,
}: MiniBarProps): ReactElement => {
  const targetWidth = (targetSeconds / maxSeconds) * 100;

  return (
    <div className="relative h-4 flex-1">
      <div
        className="absolute top-0 left-0 h-full bg-neutral-800 rounded-r-sm"
        style={{ width: `${targetWidth}%` }}
      />
      <motion.div
        className="absolute top-0 left-0 h-full rounded-r-sm"
        style={{ backgroundColor: color }}
        initial={shouldReduceMotion ? false : { width: 0 }}
        animate={{
          width: isAnimating || shouldReduceMotion ? `${targetWidth}%` : 0,
        }}
        transition={
          shouldReduceMotion
            ? { duration: 0 }
            : { duration: targetSeconds / 10, ease: "linear" }
        }
      />
      <span
        className="absolute top-1/2 -translate-y-1/2 text-[11px] font-semibold ml-2 tabular-nums whitespace-nowrap"
        style={{
          left: `${targetWidth}%`,
          color: color === BENCHMARK_CONTROL_COLOR ? "#737373" : color,
        }}
      >
        {label}
      </span>
    </div>
  );
};

MiniBar.displayName = "MiniBar";

interface MiniChartProps {
  isVisible: boolean;
  shouldReduceMotion?: boolean;
}

const MiniChart = ({
  isVisible,
  shouldReduceMotion = false,
}: MiniChartProps): ReactElement => {
  const gridLines = [0, 5, 10, 15, 20];

  return (
    <div className="w-80 py-4 pl-3 pr-5 select-none">
      <div className="relative">
        <div className="flex items-center gap-2">
          <div className="w-16 shrink-0" />
          <div className="flex-1 relative h-0">
            {gridLines.map((seconds) => (
              <div
                key={seconds}
                className="absolute top-0 border-l border-neutral-800"
                style={{
                  left: `${(seconds / BENCHMARK_TOOLTIP_MAX_SECONDS) * 100}%`,
                  height: "calc(100% + 48px)",
                  marginTop: "-2px",
                }}
              />
            ))}
          </div>
        </div>

        <div className="space-y-2 relative">
          <div className="flex items-center gap-2">
            <div className="w-16 text-right text-[10px] font-medium text-neutral-500 shrink-0 leading-tight">
              Claude Code
            </div>
            <MiniBar
              targetSeconds={BENCHMARK_TOOLTIP_CONTROL_SECONDS}
              maxSeconds={BENCHMARK_TOOLTIP_MAX_SECONDS}
              color={BENCHMARK_CONTROL_COLOR}
              label={`${BENCHMARK_TOOLTIP_CONTROL_SECONDS}s`}
              isAnimating={isVisible}
              shouldReduceMotion={shouldReduceMotion}
            />
          </div>

          <div className="flex items-center gap-2">
            <div
              className="w-16 text-right text-[10px] font-medium shrink-0 leading-tight"
              style={{ color: BENCHMARK_TREATMENT_COLOR }}
            >
              + React Grab
            </div>
            <div className="relative h-4 flex-1">
              <MiniBar
                targetSeconds={BENCHMARK_TOOLTIP_TREATMENT_SECONDS}
                maxSeconds={BENCHMARK_TOOLTIP_MAX_SECONDS}
                color={BENCHMARK_TREATMENT_COLOR}
                label=""
                isAnimating={isVisible}
                shouldReduceMotion={shouldReduceMotion}
              />
              <motion.span
                className="absolute top-1/2 -translate-y-1/2 flex items-center gap-1.5 ml-1.5"
                style={{
                  left: `${(BENCHMARK_TOOLTIP_TREATMENT_SECONDS / BENCHMARK_TOOLTIP_MAX_SECONDS) * 100}%`,
                }}
                initial={shouldReduceMotion ? false : { opacity: 0 }}
                animate={{ opacity: isVisible || shouldReduceMotion ? 1 : 0 }}
                transition={
                  shouldReduceMotion
                    ? { duration: 0 }
                    : { delay: 0.8, duration: 0.3 }
                }
              >
                <span
                  className="text-[11px] font-semibold tabular-nums"
                  style={{ color: BENCHMARK_TREATMENT_COLOR }}
                >
                  {BENCHMARK_TOOLTIP_TREATMENT_SECONDS}s
                </span>
                <span className="text-[10px] font-bold text-emerald-400">
                  {BENCHMARK_TOOLTIP_SPEEDUP_FACTOR}× faster
                </span>
              </motion.span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-2">
          <div className="w-16 shrink-0" />
          <div className="flex-1 relative h-4">
            {gridLines.map((seconds) => (
              <span
                key={seconds}
                className="absolute text-[9px] text-neutral-600 -translate-x-1/2"
                style={{
                  left: `${(seconds / BENCHMARK_TOOLTIP_MAX_SECONDS) * 100}%`,
                }}
              >
                {seconds}s
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

MiniChart.displayName = "MiniChart";

export const BenchmarkTooltip = ({
  href,
  children,
  className,
}: BenchmarkTooltipProps): ReactElement => {
  const shouldReduceMotion = Boolean(useReducedMotion());
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const handleOpenChange = (open: boolean) => {
    setIsHovered(open);
    setIsVisible(open);
  };

  return (
    <HoverCard
      openDelay={TOOLTIP_HOVER_DELAY_MS}
      closeDelay={0}
      onOpenChange={handleOpenChange}
    >
      <span className="relative inline-block">
        <HoverCardTrigger asChild>
          <Link href={href} rel="noreferrer" className={className}>
            {children}
          </Link>
        </HoverCardTrigger>
      </span>
      <HoverCardContent
        forceMount
        className="z-50 border-neutral-800 bg-[#0a0a0a] p-0"
        align="center"
        sideOffset={8}
      >
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={
                shouldReduceMotion ? false : { opacity: 0, y: 8, scale: 0.96 }
              }
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={
                shouldReduceMotion
                  ? undefined
                  : { opacity: 0, y: 4, scale: 0.98 }
              }
              transition={
                shouldReduceMotion
                  ? { duration: 0 }
                  : { duration: 0.15, ease: "easeOut" }
              }
              style={{ transformOrigin: "top center" }}
              className="overflow-hidden rounded-lg border border-neutral-800"
            >
              <MiniChart
                isVisible={isVisible}
                shouldReduceMotion={shouldReduceMotion}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </HoverCardContent>
    </HoverCard>
  );
};

BenchmarkTooltip.displayName = "BenchmarkTooltip";
